import React, { useState } from 'react'
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import { usePlannerMode } from '../../context/PlannerModeContext'
import { useDevice } from '../../hooks/useDevice'
import useAppUtilities from '../../hooks/useAppUtilities'
import { ROUTES } from '../../helpers/routes-helper'
import { getWebOrigin } from '../../helpers/referral-helper'
import { logEvent } from '../../analytics/analytics'
import { errorLog } from '../../utils/log-helper'

/**
 * The persistent planner bar — the always-visible proof that planner mode is on.
 *
 * While the user browses in planner-selection mode this sits above the content (clearing the mobile
 * bottom nav via the same `calc(84px + env(safe-area-inset-bottom))` offset the group FAB uses) and
 * shows the running count plus the one action that matters: share the link. It also carries the
 * explicit exit, so planner mode can never be a state the user is stuck in.
 *
 * Rendered once, app-wide, from the layout — so the count follows the user across home, search and
 * restaurant surfaces without any of them knowing planner mode exists.
 */
/**
 * Height the bar occupies. It is `position: fixed`, so it contributes NOTHING to layout — the
 * scroll container has to reserve this much bottom padding while planner mode is on, or the bar
 * permanently covers the last stretch of every page with no way to scroll past it (see
 * WebMobileLayout).
 */
export const PLANNER_BAR_HEIGHT_PX = 72

const PlannerModeBar: React.FC = () => {
  const planner = usePlannerMode()
  const { isHybrid } = useDevice()
  const { navigate } = useAppUtilities()
  const [sharing, setSharing] = useState(false)
  // A failed share used to be swallowed into the log, so the button read as dead. Surfaced here.
  const [shareError, setShareError] = useState<string | null>(null)

  if (!planner?.active || !planner.shareToken) {
    return null
  }

  const { candidateCount, shareToken, busy } = planner
  // NOT window.location.origin: inside the native shell that is `capacitor://localhost`, which
  // produces a link no recipient can open. getWebOrigin() returns the real https origin (same helper
  // the referral and dine-session invites use).
  const shareUrl = `${getWebOrigin()}${ROUTES.DINE}/${shareToken}`

  const handleShare = async (): Promise<void> => {
    setSharing(true)
    const nav = typeof navigator !== 'undefined' ? navigator : undefined
    // One self-contained string. Passing `text` and `url` separately lets a share target jam them
    // together (see the same note on the dine-session copy button).
    const shareText = `I picked a few places — vote for the one you want: ${shareUrl}`
    let method: 'native' | 'copy_link' | null = null
    try {
      if (nav?.share) {
        try {
          await nav.share({ title: 'Plan dinner with me', text: shareText })
          method = 'native'
        } catch (shareError) {
          // A dismissed sheet is a user choice — stop here, don't silently copy behind their back.
          if ((shareError as Error)?.name === 'AbortError') {
            return
          }
          // Anything else (notably the iOS WKWebView, where share is often present but rejects)
          // falls through to the clipboard rather than leaving the button dead.
          errorLog('[PlannerModeBar] native share failed, falling back', shareError)
        }
      }
      if (!method && nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(shareUrl)
        method = 'copy_link'
      }
      if (!method) {
        setShareError('Could not share the link. Try again from the planner page.')
        return
      }
      planner.markPlannerShared(method)
      logEvent('session_link_copied', {
        share_token: shareToken,
        method,
        screen_name: 'planner_bar',
      })
      // Land the creator on their own planner: the share is done, the next job is watching votes
      // come in. This is also the creator self-open the funnel measures.
      navigate(`${ROUTES.DINE}/${shareToken}`)
    } catch (e) {
      errorLog('[PlannerModeBar] share failed', e)
      setShareError('Could not share the link. Try again from the planner page.')
    } finally {
      setSharing(false)
    }
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: isHybrid ? 'calc(84px + env(safe-area-inset-bottom))' : 0,
        zIndex: 1250,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        backgroundColor: 'background.paper',
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: '0px -4px 16px -6px rgba(0,0,0,0.28)',
      }}
    >
      <GroupsOutlinedIcon color="primary" />
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {candidateCount === 0
            ? 'Tap the group icon on any restaurant'
            : `${candidateCount} restaurant${candidateCount === 1 ? '' : 's'} added`}
        </Typography>
        <Typography
          variant="caption"
          color={shareError ? 'error' : 'text.secondary'}
          noWrap
        >
          {shareError ??
            (candidateCount === 0
              ? 'Add a few, then share one link.'
              : 'Share one link. Everyone votes.')}
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="small"
        disabled={candidateCount === 0 || busy || sharing}
        onClick={() => {
          void handleShare()
        }}
        sx={{ textTransform: 'none', borderRadius: 2, flexShrink: 0 }}
      >
        {sharing ? (
          <CircularProgress size={18} color="inherit" />
        ) : (
          'Share with friends'
        )}
      </Button>
      <IconButton
        size="small"
        aria-label="Exit planner mode"
        onClick={() => planner.exitPlannerMode('user_exit')}
        sx={{ flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

export default PlannerModeBar
